import {
  Children,
  Component,
  ComponentClass,
  Fragment,
  ReactNode,
} from 'react';

import AnimateHeight from 'react-animate-height';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import InlineMessage from '@atlaskit/inline-message';
import { Inline } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';

import { ExpandablePanel } from '../expandable-panel';
import getIsMobileView from '../is-mobile';
import { SidebarChildContext } from '../sidebar';
import { ExpanderOnChangeEvent } from '../types';

import * as styles from './styles';

export type ExpanderProps = {
  children?: ReactNode;
  hasError?: boolean;
  icon?: JSX.Element;
  isCollapsed?: boolean;
  defaultIsCollapsed?: boolean;
  isLoading?: boolean;
  label: ReactNode;
  iconSecondary?: JSX.Element;
  onChange?: (event: ExpanderOnChangeEvent) => void;
  sectionAriaLabel?: string;
  headingAriaLabel?: string;
  renderSecondaryIcon?: (isCollapsed: boolean) => ReactNode;
  dataTestId?: string;
  isPanelBodyOverflowVisible?: boolean;
  panelBodyPadding?: string;
  isPrPage?: boolean;
  localStorageKey?: string;
  shouldRenderHeader?: boolean;
};

type ExpanderState = {
  isCollapsed: boolean;
};

type DefaultProps = {
  hasError: boolean;
  defaultIsCollapsed: boolean;
  isLoading: boolean;
  onChange: (event: ExpanderOnChangeEvent) => void;
  localStorageKey: string;
};

type Props = ExpanderProps & DefaultProps;

class Expander extends Component<Props, ExpanderState> {
  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    hasError: false,
    defaultIsCollapsed: false,
    isLoading: false,
    onChange: () => {},
    localStorageKey: '',
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      isCollapsed:
        props.isCollapsed !== undefined
          ? props.isCollapsed
          : props.defaultIsCollapsed,
    };
  }

  // @ts-ignore TODO: fix noImplicitAny error here
  static getDerivedStateFromProps(prevProps, state) {
    if (prevProps.isCollapsed === undefined) {
      // then the expander is not controlled, leave state unmodified
      return null;
    } else if (prevProps.isCollapsed !== state.isCollapsed) {
      // then the expander is controlled, and syncing is off
      return {
        isCollapsed: !!prevProps.isCollapsed,
      };
    }
    // eslint requires returning
    return null;
  }

  isControlled = () => this.props.isCollapsed !== undefined;

  isCollapsed = (props: Props): boolean => {
    const isCollapsedByProps = this.isControlled() && this.props.isCollapsed;
    return (
      !this.isExpandable(props) || isCollapsedByProps || this.state.isCollapsed
    );
  };
  // Use `.toArray().length` because `.count()` includes children with the
  // value `false` in its calculation
  isExpandable = (props: Props): boolean =>
    Children.toArray(props.children).length > 0 &&
    !props.isLoading &&
    !this.props.hasError;

  toggleCollapse = (): void => {
    if (!this.isExpandable(this.props)) {
      return;
    }

    const nextState = !this.state.isCollapsed;
    this.props.onChange({ isCollapsed: nextState });

    this.setState(prevState => ({
      isCollapsed: !prevState.isCollapsed,
    }));
  };

  renderSecondaryIcon = (isCollapsed: boolean) => {
    const { iconSecondary = null, renderSecondaryIcon } = this.props;

    if (renderSecondaryIcon) {
      return renderSecondaryIcon(isCollapsed);
    }

    return isCollapsed && iconSecondary ? (
      <styles.IconSecondary>{iconSecondary}</styles.IconSecondary>
    ) : null;
  };

  renderHeading() {
    const { children, hasError, icon, isLoading, label, isPrPage } = this.props;

    if (isLoading) {
      return (
        <styles.Spinner>
          <Spinner size="small" testId="expander-loading-spinner" />
        </styles.Spinner>
      );
    }

    const errorIcon = () => (
      <styles.WarningWrapper>
        {children ? (
          <InlineMessage placement="bottom-start" appearance="warning">
            {children}
          </InlineMessage>
        ) : (
          <StatusWarningIcon spacing="spacious" label="" />
        )}
      </styles.WarningWrapper>
    );

    const isCollapsed = this.isCollapsed(this.props);

    return isPrPage ? (
      // PanelHeader wrapper added for a11y
      // Sidebar panel headers should be h2
      <styles.PanelHeader style={{ fontWeight: 'inherit' }}>
        <Inline alignBlock="center" grow="fill" space="space.050">
          {icon ? <>{icon}</> : null}
          {hasError ? (
            errorIcon()
          ) : (
            <Inline alignBlock="center" grow="fill" spread="space-between">
              <Inline space="space.050">{label}</Inline>
              {this.renderSecondaryIcon(isCollapsed)}
            </Inline>
          )}
        </Inline>
      </styles.PanelHeader>
    ) : (
      <Fragment>
        {icon ? <styles.Icon>{icon}</styles.Icon> : null}
        {hasError ? (
          errorIcon()
        ) : (
          <Fragment>
            <styles.PanelHeadingLabel>{label}</styles.PanelHeadingLabel>
            {this.renderSecondaryIcon(isCollapsed)}
          </Fragment>
        )}
        {this.isExpandable(this.props) && (
          <styles.Chevron>
            {isCollapsed ? (
              <ChevronRightIcon label="" spacing="spacious" size="small" />
            ) : (
              <ChevronDownIcon label="" spacing="spacious" size="small" />
            )}
          </styles.Chevron>
        )}
      </Fragment>
    );
  }

  render() {
    const {
      sectionAriaLabel,
      headingAriaLabel,
      isCollapsed,
      dataTestId,
      isPanelBodyOverflowVisible,
      panelBodyPadding,
      isPrPage,
      localStorageKey,
      children,
      shouldRenderHeader = true,
    } = this.props;

    const isMobileView = getIsMobileView();

    const thisIsCollapsed = this.isControlled()
      ? !!isCollapsed
      : this.state.isCollapsed;

    const fullDataTestId = dataTestId
      ? `${thisIsCollapsed ? 'expand' : 'collapse'}-${dataTestId}`
      : headingAriaLabel;

    return (
      <SidebarChildContext.Consumer>
        {index =>
          isPrPage ? (
            <section aria-label={sectionAriaLabel}>
              {index > 0 && !isMobileView ? <styles.GrayDivider /> : null}
              <Inline grow="fill" alignInline="stretch">
                <ExpandablePanel
                  storageKey={localStorageKey}
                  header={this.renderHeading()}
                  onExpandChange={this.toggleCollapse}
                  defaultExpanded={!thisIsCollapsed}
                >
                  {children}
                </ExpandablePanel>
              </Inline>
            </section>
          ) : (
            <styles.Panel aria-label={sectionAriaLabel}>
              {shouldRenderHeader && (
                <styles.PanelHeadingWrapper>
                  <styles.PanelHeading
                    isCollapsed={thisIsCollapsed}
                    aria-controls={`expanderId-${index}`}
                    aria-expanded={!thisIsCollapsed}
                    aria-label={headingAriaLabel}
                    className="sidebar-expander-panel-heading"
                    isExpandable={this.isExpandable(this.props)}
                    onClick={this.toggleCollapse}
                    data-testid={fullDataTestId}
                  >
                    {this.renderHeading()}
                  </styles.PanelHeading>
                </styles.PanelHeadingWrapper>
              )}
              {this.isExpandable(this.props) && (
                <styles.AnimateHeightContainer
                  allowMaxHeight={!isPanelBodyOverflowVisible}
                >
                  <AnimateHeight
                    duration={200}
                    easing="linear"
                    height={thisIsCollapsed ? 0 : 'auto'}
                    // @ts-ignore
                    // Fixed in react-animate-height v2.0.23
                    id={`expanderId-${index}`}
                  >
                    <styles.ExpanderPanelBody
                      isOverflowVisible={isPanelBodyOverflowVisible}
                      panelBodyPadding={panelBodyPadding}
                    >
                      {children}
                    </styles.ExpanderPanelBody>
                  </AnimateHeight>
                </styles.AnimateHeightContainer>
              )}
            </styles.Panel>
          )
        }
      </SidebarChildContext.Consumer>
    );
  }
}
// eslint-disable-next-line import/no-default-export
export default Expander as ComponentClass<ExpanderProps>;
